import React from "react";
import { Button } from "antd";
import { map } from "lodash";
import { AiccSandboxGrammarResult } from "types/model/aiccPromptSandbox";
import { SandboxBubble } from "./conversation";
import GradingCard from "./GradingCard";
import { grammarHighlights } from "./grammarHighlights";
import {
  ACTION_NO_ANSWER,
  CHECK_GRAMMAR,
  GRAMMAR_CLEAN,
  GRAMMAR_CORRECTED,
  GRAMMAR_SCOPE,
  GRAMMAR_SKIPPED,
  THINKING_LABEL,
  TRANSLATION_LABEL,
} from "../configs";
import styles from "../Sandbox.module.scss";

interface Props {
  bubbles: SandboxBubble[];
  isThinking: boolean;
  pendingUserText?: string;
  hasCheck: boolean;
  corrections: Record<number, AiccSandboxGrammarResult | null>;
  correctingIndex?: number;
  onCorrect: (index: number, text: string) => void;
}

function GrammarResult({ result }: { result: AiccSandboxGrammarResult }) {
  if (!result.hasErrors) {
    return <div className={styles.grammarClean}>{GRAMMAR_CLEAN}</div>;
  }

  return (
    <div className={styles.grammar}>
      {!!result.correctedMessage && (
        <div>
          <span className={styles.grammarLabel}>{GRAMMAR_CORRECTED}</span> {result.correctedMessage}
        </div>
      )}
      {map(grammarHighlights(result.highlightedErrors), ({ originalText, correctedText, explanation }, index) => (
        <div key={index} className={styles.grammarError}>
          {!!originalText && <span className={styles.grammarOriginal}>{originalText}</span>}
          {!!correctedText && <span>{` → ${correctedText}`}</span>}
          {!!explanation && <span className={styles.grammarExplanation}>{explanation}</span>}
        </div>
      ))}
      <div className={styles.grammarScope}>{GRAMMAR_SCOPE}</div>
    </div>
  );
}

function ChatThread({
  bubbles,
  isThinking,
  pendingUserText,
  hasCheck,
  corrections,
  correctingIndex,
  onCorrect,
}: Props) {
  return (
    <div className={styles.thread}>
      {map(bubbles, ({ role, text, nativeText, translation, transliteration, grading, grammar, isGraded }, index) => (
        <div key={index} className={role === "user" ? styles.bubbleRowUser : styles.bubbleRow}>
          <div className={styles.bubbleColumn}>
            <div className={role === "user" ? styles.bubbleUser : styles.bubble}>
              {text}
              {!!nativeText && <div className={styles.transliteration}>{nativeText}</div>}
              {!!transliteration && <div className={styles.transliteration}>{transliteration}</div>}
              {!!translation && (
                <div className={styles.transliteration}>
                  <span className={styles.bubbleAsideLabel}>{TRANSLATION_LABEL}</span>
                  {translation}
                </div>
              )}
            </div>

            {!!grading && <GradingCard grading={grading} />}

            {!!grammar && <GrammarResult result={grammar} />}

            {isGraded && !grammar && <div className={styles.grammarClean}>{GRAMMAR_SKIPPED}</div>}

            {hasCheck && role === "user" && !(index in corrections) && (
              <Button
                type="link"
                className={styles.checkGrammar}
                loading={correctingIndex === index}
                onClick={() => onCorrect(index, nativeText || text)}
              >
                {CHECK_GRAMMAR}
              </Button>
            )}

            {index in corrections &&
              (corrections[index] ? (
                <GrammarResult result={corrections[index] as AiccSandboxGrammarResult} />
              ) : (
                <div className={styles.grammarClean}>{ACTION_NO_ANSWER}</div>
              ))}
          </div>
        </div>
      ))}

      {!!pendingUserText && (
        <div className={styles.bubbleRowUser}>
          <div className={styles.bubbleColumn}>
            <div className={styles.bubbleUser}>{pendingUserText}</div>
          </div>
        </div>
      )}

      {isThinking && (
        <div className={styles.bubbleRow}>
          <div className={styles.bubble} aria-label={THINKING_LABEL}>
            <span className={styles.thinking}>
              <i />
              <i />
              <i />
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatThread;
